import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@treezor/design-system';
import { useParams } from 'react-router-dom';

import { IEditionMetadataModalProps } from '@/features/cards/store/types/restrictionGroups.types';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { useConfirmationDialog } from '@/hooks/useConfirmationModal';
import { deletePredefRestrictionsGroupMetadata } from '@/store/actions/predef-restrictions-group.actions';
import { openModal } from '@/store/actions/core.actions';

interface IRestrictionGroupManagementActions {
  mid: string;
}

export const RestrictionGroupManagementActions = ({ mid }: IRestrictionGroupManagementActions) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { groupId } = useParams();

  const restrictionGroups = useAppSelector((state) => state.predefRestrictionsGroup.restrictionsGroups.mid?.elements);

  const restrictionGroup = useMemo(
    () => restrictionGroups.find((restrictionGroup) => restrictionGroup.group.id === Number(groupId)),
    [groupId, restrictionGroups],
  );

  const toggleEditRestrictionModal = () => {
    if (restrictionGroup?.group.id && restrictionGroup?.group.name)
      dispatch(
        openModal<IEditionMetadataModalProps>('editRestrictionModal', {
          restrictionsGroupId: restrictionGroup.group.id,
          restriction: mid,
          name: restrictionGroup.group.name,
        }),
      );
  };

  const deleteRestriction = () => {
    const restrictions = [...(restrictionGroup?.group.restrictions || '')];
    const index = restrictions?.findIndex((midIterator) => midIterator === mid);

    if (index === undefined || index === -1) return;

    restrictions.splice(index, 1);
    restrictionGroup &&
      dispatch(
        deletePredefRestrictionsGroupMetadata({
          ...restrictionGroup.group,
          restrictions,
        }),
      );
  };

  const RemoveRestriction = useConfirmationDialog({
    title: t('pages.administration.restrictionGroup.actions.metadataRestriction.removeModal.header', { mid }),
    description: t('pages.administration.restrictionGroup.actions.metadataRestriction.removeModal.description', {
      mid,
      name: restrictionGroup?.group.name,
    }),
    cancel: { label: t('pages.administration.restrictionGroup.actions.metadataRestriction.removeModal.cancel') },
    confirm: {
      label: t('pages.administration.restrictionGroup.actions.metadataRestriction.removeModal.ok'),
      action: () => deleteRestriction(),
      color: 'danger',
    },
  });

  return (
    <>
      <div className="flex items-center w-full gap-3">
        <Button id="delete-button-mid" size="s" variant="ghost" color="danger" onClick={RemoveRestriction.toggle}>
          {t('pages.cores.delete').toUpperCase()}
        </Button>
        <Button
          id="edit-button-mid"
          variant="ghost"
          size="s"
          onClick={toggleEditRestrictionModal}
          disabled={!restrictionGroup?.group.id || !restrictionGroup?.group.name}
        >
          {t('pages.cores.update').toUpperCase()}
        </Button>
      </div>

      <RemoveRestriction.ConfirmationDialog />
    </>
  );
};
